<template>
    <div class="dg-wrap" v-if="employee">
        <!-- Appointment Letter Section -->
        <div class="ee-section">
            <div class="ee-section-head"><i class="fas fa-file-signature ee-section-icon"></i><span>Appointment Letter</span></div>
            <form @submit.prevent="generateLetter('Appointment')">
                <div class="ee-grid">
                    <div class="ee-field">
                        <custom-input v-model="totalSickLeaves" type="number" required label="Total Sick Leaves" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="companyNoticePeriodDays" type="number" required label="Company Notice Period Days" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="employeeNoticePeriodDays" type="number" required label="Employee Notice Period Days" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="highCourtName" type="text" required label="High Court Name" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="highCourtState" type="text" required label="High Court State" />
                    </div>
                </div>
                <div class="dg-multi-section">
                    <MultiTextAdder v-if="initialized" title="Duties" v-model="duties" />
                    <MultiTextAdder v-if="initialized" title="Responsibilities" v-model="responsibilities" />
                </div>

                <!-- Missing fields warning -->
                <div class="dg-missing" v-if="missingAppointmentLetterFields.length">
                    <div class="dg-missing-title"><i class="fas fa-exclamation-triangle"></i> Please fix below issues to continue:</div>
                    <ul class="dg-missing-list">
                        <li v-for="field of missingAppointmentLetterFields" :key="field">Missing <strong>{{ field }}</strong> in Personal Details</li>
                    </ul>
                </div>

                <!-- Last generated info -->
                <div class="dg-generated-info" v-if="employee.appointmentLetterLastGeneratedDate">
                    <i class="fas fa-clock"></i> Last generated on {{ formatIndianDate(employee.appointmentLetterLastGeneratedDate) }}
                </div>

                <!-- Action buttons -->
                <div class="dg-actions" v-if="!missingAppointmentLetterFields.length">
                    <button type="submit" class="dg-btn dg-btn-dark" :disabled="generatingAppointment">
                        <i :class="generatingAppointment ? 'fas fa-spinner fa-spin' : employee.appointmentLetterPdf ? 'fas fa-refresh' : 'fas fa-check'"></i>
                        {{ generatingAppointment ? 'Generating...' : employee.appointmentLetterPdf ? 'Regenerate' : 'Generate Appointment Letter' }}
                    </button>
                    <button type="button" class="dg-btn dg-btn-primary" v-if="employee.appointmentLetterPdf" @click="downloadLetter(employee.appointmentLetterPdf)">
                        <i class="fas fa-download"></i> Download
                    </button>
                </div>
            </form>
        </div>

        <!-- Offer Letter Section -->
        <div class="ee-section">
            <div class="ee-section-head"><i class="fas fa-file-contract ee-section-icon"></i><span>Offer Letter</span></div>
            <form @submit.prevent="generateLetter('Offer')">
                <div class="ee-grid">
                    <div class="ee-field">
                        <custom-input v-model="offeredJobByPersonName" type="text" required label="Offered By Person Name" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="offeredJobByPersonPosition" type="text" required label="Position of Person Offering" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="desiredJobStartingDate" type="date" :min="dateToday" required label="Desired Starting Date" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="desiredJobStartingTime" type="time" required label="Desired Starting Time" />
                    </div>
                    <div class="ee-field">
                        <custom-input v-model="lastDateForJobOfferAcceptance" :min="dateToday" type="date" required label="Last Date to Accept Offer" />
                    </div>
                </div>
                <div class="dg-multi-section">
                    <MultiTextAdder v-if="initialized" title="Benefits" v-model="benefits" />
                </div>

                <!-- Missing fields warning -->
                <div class="dg-missing" v-if="missingOfferLetterFields.length">
                    <div class="dg-missing-title"><i class="fas fa-exclamation-triangle"></i> Please fix below issues to continue:</div>
                    <ul class="dg-missing-list">
                        <li v-for="field of missingOfferLetterFields" :key="field">Missing <strong>{{ field }}</strong> in Personal Details</li>
                    </ul>
                </div>

                <!-- Last generated info -->
                <div class="dg-generated-info" v-if="employee.offerLetterLastGeneratedDate">
                    <i class="fas fa-clock"></i> Last generated on {{ formatIndianDate(employee.offerLetterLastGeneratedDate) }}
                </div>

                <!-- Action buttons -->
                <div class="dg-actions" v-if="!missingOfferLetterFields.length">
                    <button type="submit" class="dg-btn dg-btn-dark" :disabled="generatingOfferLetter">
                        <i :class="generatingOfferLetter ? 'fas fa-spinner fa-spin' : employee.offerLetterPdf ? 'fas fa-refresh' : 'fas fa-check'"></i>
                        {{ generatingOfferLetter ? 'Generating...' : employee.offerLetterPdf ? 'Regenerate' : 'Generate Offer Letter' }}
                    </button>
                    <button type="button" class="dg-btn dg-btn-primary" v-if="employee.offerLetterPdf" @click="downloadLetter(employee.offerLetterPdf)">
                        <i class="fas fa-download"></i> Download
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { daysToMs, formatIndianDate, getAssetUrlPath } from "../../utils/common"
import { http, serverURL } from "../../utils/http"
import CustomInput from "../crud/custom-input.vue"
import MultiTextAdder from "./MultiTextAdder.vue"

export default {
    props: {
        employee: {
            type: Object,
            default: null,
        },
    },
    components: { MultiTextAdder, CustomInput },
    data() {
        return {
            dateToday: new Date().toISOString().slice(0, 10),
            showAppointmentLetterModal: false,
            showOfferLetterModal: false,
            generatingAppointment: false,
            generatingOfferLetter: false,
            employees: [],

            // offer letter variables
            offeredJobByPersonName: "Hemang Mehta",
            offeredJobByPersonPosition: "HR Manager",
            desiredJobStartingDate: null,
            desiredJobStartingTime: null,
            lastDateForJobOfferAcceptance: null,

            // appointment letter variables:
            totalSickLeaves: null,
            companyNoticePeriodDays: null,
            employeeNoticePeriodDays: null,
            highCourtName: null,
            highCourtState: null,

            benefits: [],
            duties: [],
            responsibilities: [],
            initialized: false,
            missingAppointmentLetterFields: [],
            missingOfferLetterFields: [],
        }
    },
    async mounted() {
        this.benefits = this.employee.positionBenefitsCsv
            ? this.employee.positionBenefitsCsv.split(",")
            : []
        this.duties = this.employee.dutiesCsv ? this.employee.dutiesCsv.split(",") : []
        this.responsibilities = this.employee.responsibilitiesCsv
            ? this.employee.responsibilitiesCsv.split(",")
            : []

        // initialize data

        this.offeredJobByPersonName = this.employee.offeredJobByPersonName || "Hemang Mehta"
        this.offeredJobByPersonPosition = this.employee.offeredJobByPersonPosition || "HR Manager"
        this.desiredJobStartingDate = this.employee.desiredJobStartingDate || null
        this.desiredJobStartingTime = this.employee.desiredJobStartingTime || null
        this.lastDateForJobOfferAcceptance = this.employee.lastDateForJobOfferAcceptance || null

        // appointment letter variables;
        this.totalSickLeaves = this.employee.totalSickLeaves || null
        this.companyNoticePeriodDays = this.employee.companyNoticePeriodDays || null
        this.employeeNoticePeriodDays = this.employee.employeeNoticePeriodDays || null
        this.highCourtName = this.employee.highCourtName || null
        this.highCourtState = this.employee.highCourtState || null

        // check if any missing fields
        const {
            workLocationId,

            salutation,
            attendanceShiftId,
            supervisorUserId,

            city,
            state,
            country,
            companyId,
            designationId,
        } = this.employee

        this.missingAppointmentLetterFields = []
        this.missingOfferLetterFields = []

        if (!workLocationId) {
            this.missingAppointmentLetterFields.push("Work Location")
        }
        // other fields required in both
        if (!salutation) {
            this.missingAppointmentLetterFields.push("Salutation")
            this.missingOfferLetterFields.push("Salutation")
        }
        if (!supervisorUserId) {
            this.missingAppointmentLetterFields.push("Supervisor/Reporter")
            this.missingOfferLetterFields.push("Supervisor/Reporter")
        }
        if (!attendanceShiftId) {
            this.missingAppointmentLetterFields.push("Attendance Shift")
            this.missingOfferLetterFields.push("Attendance Shift")
        }
        if (!city) {
            this.missingAppointmentLetterFields.push("City")
            this.missingOfferLetterFields.push("City")
        }
        if (!state) {
            this.missingAppointmentLetterFields.push("State")
            this.missingOfferLetterFields.push("State")
        }
        if (!country) {
            this.missingAppointmentLetterFields.push("Country")
            this.missingOfferLetterFields.push("Country")
        }
        if (!companyId) {
            this.missingAppointmentLetterFields.push("Salary Company")
            this.missingOfferLetterFields.push("Salary Company")
        }
        if (!designationId) {
            this.missingAppointmentLetterFields.push("Designation")
            this.missingOfferLetterFields.push("Designation")
        }

        this.initialized = true
    },
    watch: {
        desiredJobStartingDate(e) {
            this.lastDateForJobOfferAcceptance = new Date(new Date(e).getTime() + daysToMs(5))
                .toISOString()
                .slice(0, 10)
        },
    },
    methods: {
        formatIndianDate,
        async generateLetter(type) {
            try {
                if (!this.employee.salutation) {
                    return this.$toast.error("Please update salutation in Personal Details!")
                }
                if (this.generatingOfferLetter || this.generatingAppointment) return
                const isOfferLetter = type.toLowerCase() == "offer"
                if (isOfferLetter) {
                    this.generatingOfferLetter = true
                } else {
                    this.generatingAppointment = true
                }
                const res = await http.post(
                    `/employees/${this.employee.id}/generate-${type.toLowerCase()}-letter/`,
                    {
                        // for offer letter
                        offeredJobByPersonName: this.offeredJobByPersonName,
                        offeredJobByPersonPosition: this.offeredJobByPersonPosition,
                        desiredJobStartingDate: this.desiredJobStartingDate,
                        desiredJobStartingTime: this.desiredJobStartingTime,
                        lastDateForJobOfferAcceptance: this.lastDateForJobOfferAcceptance,

                        positionBenefitsCsv: this.benefits,

                        // appointment letter variables:
                        totalSickLeaves: this.totalSickLeaves,
                        companyNoticePeriodDays: this.companyNoticePeriodDays,
                        employeeNoticePeriodDays: this.employeeNoticePeriodDays,
                        highCourtName: this.highCourtName,
                        highCourtState: this.highCourtState,

                        dutiesCsv: this.duties,
                        responsibilitiesCsv: this.responsibilities,
                    }
                )
                this.$toast.success(`${type} Letter Generated!`)

                const createdBefore =
                    (!isOfferLetter && this.employee.appointmentLetterPdf) ||
                    (isOfferLetter && this.employee.offerLetterPdf)

                if (isOfferLetter) {
                    this.employee.offerLetterLastGeneratedDate = new Date()
                } else {
                    this.employee.appointmentLetterLastGeneratedDate = new Date()
                }

                // if (!createdBefore) {
                //     window.location.href = `${this.$route.path}?active_tab=WORK`
                // }
                this.generatingOfferLetter = false
                this.generatingAppointment = false
                window.location.href = `${this.$route.path}?active_tab=WORK`
            } catch (err) {
                console.log("error in generating appointment letter", err)
            }
        },
        async downloadLetter(path) {
            window.open(getAssetUrlPath(path), "_blank")
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.dg-wrap { margin-top: 8px; }

/* Reuse parent ee-section/ee-grid/ee-field styles */
.ee-section { background:$cardBg; border:1px solid $gray100; border-radius:$radiusMd; margin-bottom:16px; overflow:hidden; }
.ee-section-head { display:flex; align-items:center; gap:8px; padding:12px 16px; font-size:13px; font-weight:700; color:$gray700; border-bottom:1px solid $gray100; }
.ee-section-icon { color:$primaryColor; font-size:14px; }
.ee-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; padding:16px; }
.ee-field { min-width:0; overflow:hidden; }

/* Multi text adder section */
.dg-multi-section { padding: 0 16px 12px; }

/* Missing fields warning */
.dg-missing { margin: 0 16px 12px; padding: 10px 14px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; }
.dg-missing-title { font-size: 12.5px; font-weight: 600; color: #dc2626; margin-bottom: 4px; i { margin-right: 5px; } }
.dg-missing-list { margin: 4px 0 0; padding-left: 18px; list-style: disc; li { font-size: 12px; color: #b91c1c; line-height: 1.7; strong { color: #991b1b; } } }

/* Generated info */
.dg-generated-info { margin: 0 16px 12px; padding: 8px 12px; background: $gray50; border-radius: 6px; font-size: 12px; color: $gray500; i { margin-right: 4px; color: $gray400; } }

/* Action buttons */
.dg-actions { display: flex; gap: 8px; padding: 0 16px 16px; }
.dg-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: inherit; transition: all .15s; &:disabled { opacity: 0.6; cursor: not-allowed; } }
.dg-btn-dark { background: $gray800; color: white; &:hover:not(:disabled) { background: $gray900; } }
.dg-btn-primary { background: $primaryColor; color: white; &:hover { background: $primaryDark; } }

/* form-control overrides within this component */
::v-deep .form-control {
    border: 1px solid $gray200;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 13px;
    transition: border .15s;
    &:focus { border-color: $primaryColor; box-shadow: 0 0 0 3px rgba(0,94,137,.08); }
}
</style>
